/* @flow */

import React, {Component} from 'react';
import KaizenView from './Kaizen/KaizenView';
import ThorView from './Thor/ThorView';
import IncompatibleView from './Incompatible/IncompatibleView';
import OpenFileView from './OpenFile/OpenFileView';
import DownloadPageFooter from '../../components/DownloadPageFooter/DownloadPageFooter';
import DownloadCoachMark from '../../../../components/DownloadCoachMark/DownloadCoachMark';
import Wait from '@react/react-spectrum/Wait';
import DownloadControllerScss from './DownloadController.scss';
import {DownloadWorkflow} from '../../../../constants/appsConstants';
import {
	LOADING_WORKFLOW,
	MESSAGE_BOX_VARIANT,
	AUTO_DOWNLOAD_QUERY_PARAM,
	TAGS,
	LIST_IDS,
	GET_HELP_OPEN_SOURCES,
	DOWNLOAD_VIA_CCD_QUERY_PARAM,
	DOWNLOAD_CONTROLLER_USER_SOURCE,
	GET_HELP_STRINGS,
	LOGGED_OUT_CAMPAIGN_WORKFLOWS
} from '../../../../constants/AppPicker/appPickerConstants';
import DownloadUtil from '../../../../utils/DownloadUtil';
import AppsUtil from '../../../../utils/AppsUtil';
import CompatibilityUtil from '../../../../utils/CompatibilityUtil';
import CSSUtil from '../../../../utils/CSSUtil';
import AppPickerUtil from '../../../../utils/AppPicker/AppPickerUtil';
import HelpPopoverUtil from '../../../../utils/AppPicker/HelpPopoverUtil';
import URL from '../../../../utils/Url';
import AppPickerAnalytics from '../../../../analytics/AppPickerAnalytics';
import getConfigApps from '../../../../config';
import AppsAnalytics from '../../../../analytics/AppsAnalytics';
import {getLocalizedURL} from '@intl/adobe-locales';
import uuid from 'uuid';
import CCDUtil from '../../../../utils/CCDUtil';

type Props = {
	productId: string,
	source: string, /* Place from where the Download Controller is being used */
	locale: string,
	className?: string,
	environment: string,
	clearHelpTimeouts?: Function,
	showIncompatibilityDialog?: Function,
	openGetHelpWidget: boolean,
	hasThor: ?boolean,
	history: Object,
	analyticsData?: Object, /* Overrides for analytics fields */
	intl: Object
};

type State = {
	workflow: string,
	product: Object
};

type AnalyticObjectInterface = {
	eventObj?: {
		type?: ?string,
		subtype?: ?string,
		subcategory?: string,
		value?: ?string,
		guid?: ?string
	},
	updatedWorkflow?: string,
	contentObj?: {
		action?: string
	}
};

export default class DownloadController extends Component<Props, State> {
	_isMounted: boolean;
	downloadViaCCD: boolean;
	appPickerUrl: string;

	constructor(props: Props) {
		super(props);
		this.state = {workflow: LOADING_WORKFLOW, product: {}};
		this._isMounted = false; /* To keep track if this component is even mounted when the DownloadUtil promise resolves */
		this.downloadViaCCD = false; /* To check if the download needs to happen via CCD using secondarySapCode method */
		this.appPickerUrl = `/apps/starter/${String(AppPickerUtil.getPlanFromDownloadUrl())}`;
	}

	ccIconEvent = (autoFocusListItem: boolean) => {
		const heading = this.props.intl.formatMessage({id: GET_HELP_STRINGS.CC_CLICK_FAQ_HEADING});
		const updatingList = {
			listId: LIST_IDS.FAQ_AND_HELP,
			heading
		};
		HelpPopoverUtil.updateHelpPopoverConfig(undefined, [updatingList]);
		HelpPopoverUtil.openGetHelpPopover(GET_HELP_OPEN_SOURCES.CC_LOGO_CLICK, autoFocusListItem);
		this.handleAnalyticsEvent({
			eventObj: {
				subcategory: CCDUtil.getCampaignWorkflowSubcategory(),
				type: 'click',
				subtype: 'cclogo'
			}
		});
	}

	handleCCEnter = (event: Object) => {
		if (event.key === 'Enter') {
			this.ccIconEvent(true);
		}
	}

	chatEvent = () => {
		this.handleAnalyticsEvent({
			eventObj: {
				subcategory: 'Download',
				type: 'click',
				subtype: 'chat'
			}
		});
	}

	componentDidMount() {
		const {
			productId,
			hasThor,
			clearHelpTimeouts,
			history,
			showIncompatibilityDialog,
			source
		} = this.props;
		this._isMounted = true;
		const sapCode = AppsUtil.getSapCodeFromProductId(productId);
		// Reroute in case user hits URL directly with wrong product id or product id of a disabled product.
		if (!sapCode || AppsUtil.disableProduct(sapCode)) {
			if (AppsUtil.hasFeature('temp_cch_10855')) {
				window._uxa = window._uxa || [];
				window._uxa.push(['trackPageview', this.appPickerUrl]);
			}
			history.replace(this.appPickerUrl);
		}
		else {
			this.downloadViaCCD = (sapCode === AppPickerUtil.getDefaultDownloadProduct()) && (URL.getParam(DOWNLOAD_VIA_CCD_QUERY_PARAM.QUERY_PARAM) === DOWNLOAD_VIA_CCD_QUERY_PARAM.QUERY_VALUE);
			const downloadDelay = hasThor ? 500 : 0;
			if (source === DOWNLOAD_CONTROLLER_USER_SOURCE.LOGGED_OUT_CAMPAIGN_PAGE
				&& URL.getParam('workflow') === LOGGED_OUT_CAMPAIGN_WORKFLOWS.OPEN_FILE
				&& !hasThor
				&& URL.getParam('file')) {
				/* For open file workflow of deep linking campaign pages, when Thor is not detected, download the file  */
				if (!this._isMounted) {
					return;
				}
				DownloadUtil.setFFCAllVersionData(sapCode).then(() => {
					const product = {
						id: productId,
						sapCode,
						name : AppsUtil.getProductDisplayName(productId) || AppPickerUtil.getDisplayName(sapCode)
					};
					this.isFileDownloadSuccess = DownloadUtil.downloadFile(URL.getParam('file'));
					this.handleAnalyticsEvent({
						eventObj: {
							type: 'render',
							subtype: this.isFileDownloadSuccess ? 'downloading' : 'downloading-fail',
							value: this.isFileDownloadSuccess ? undefined : 'invalid-path'
						},
						updatedWorkflow: DownloadWorkflow.FileOpen
					});
					this.setState({workflow: DownloadWorkflow.FileOpen, product});
				});
				return;
			}

			if (CCDUtil.isRouteToPathCampaign()) {
				const path = URL.getParam('path');
				const defaultPath = URL.getParam('defaultPath');
				const product = {
					id: productId,
					sapCode,
					name : AppsUtil.getProductDisplayName(productId) || AppPickerUtil.getDisplayName(sapCode)
				};
				if (hasThor) {
					if (path) {
						DownloadUtil.routeToCCDPath(path);
						const workflow = DownloadWorkflow.Thor;
						this.setState({workflow, product});
						this.handleAnalyticsEvent({
							eventObj: {
								type: 'render',
								subtype: 'routing'
							},
							updatedWorkflow: workflow,
							contentObj: {
								action: path
							}
						});
						return;
					}
					else if (!defaultPath || !DownloadUtil.validateDefaultPathUrl(defaultPath)) {
						this.handleAnalyticsEvent({
							eventObj: {
								type: 'render',
								subtype: 'routing',
								value: 'no-path-defined'
							},
							updatedWorkflow: DownloadWorkflow.Thor
						});
					}
				}
				/*
					If path is not specified, but defaultPath is
					then redirect to defaultPath irrespective of
					whether CCD is present or not
				*/
				if (defaultPath && DownloadUtil.validateDefaultPathUrl(defaultPath)) {
					const guid = uuid.v4();
					const updatedDefaultPath = AppsUtil.openExternalLink(defaultPath, undefined, guid, undefined, undefined, '_self');
					this.handleAnalyticsEvent({
						eventObj: {
							type: 'redirect',
							subtype: 'routing',
							guid
						},
						contentObj: {
							action: updatedDefaultPath
						}
					});
					return;
				}
			}

			if (CCDUtil.isProductRouteToPathCampaign(sapCode)) {
				const path = URL.getParam('path');
				const defaultPath = URL.getParam('defaultPath');
				const product = {
					id: productId,
					sapCode,
					name : AppsUtil.getProductDisplayName(productId) || AppPickerUtil.getDisplayName(sapCode)
				};
				if (hasThor) {
					// CCD present
					if (path
						&& DownloadUtil.getValidUriFromUrl(path)
						&& !CompatibilityUtil.isOSDeprecated()
						&& (CompatibilityUtil.isAppSupported(sapCode) || CompatibilityUtil.isAnyOlderVersionSupported(sapCode))) {
						// valid path and compatible OS
						DownloadUtil.routeToProductPath(path);
						const workflow = DownloadWorkflow.Thor;
						this.setState({workflow, product});
						this.handleAnalyticsEvent({
							eventObj: {
								type: 'render',
								subtype: 'routing'
							},
							updatedWorkflow: workflow,
							contentObj: {
								action: path
							}
						});
						return;
					}
				}
				else {
					// CCD not present
					if (!(path && DownloadUtil.getValidUriFromUrl(path))) {
						// If path is not valid
						if (defaultPath && DownloadUtil.validateDefaultPathUrl(defaultPath)) {
							// If default path is present, redirect to it
							const guid = uuid.v4();
							const updatedDefaultPath = AppsUtil.openExternalLink(defaultPath, undefined, guid, undefined, undefined, '_self');
							this.handleAnalyticsEvent({
								eventObj: {
									type: 'redirect',
									subtype: 'routing',
									guid
								},
								contentObj: {
									action: updatedDefaultPath
								}
							});
							return;
						}

						// If default path not present, send this event
						this.handleAnalyticsEvent({
							eventObj: {
								type: 'render',
								subtype: 'routing',
								value: 'no-path-defined'
							},
							updatedWorkflow: DownloadWorkflow.Kaizen
						});
					}
				}
			}

			DownloadUtil.downloadProduct({sapcode:sapCode, hasThor, downloadDelay, isAllApps: this.downloadViaCCD}).then((workflow) => {
				if (!this._isMounted) {
					return;
				}
				if (this.props.source === DOWNLOAD_CONTROLLER_USER_SOURCE.LOGGED_OUT_KCCC_PAGE && workflow === DownloadWorkflow.Incompatible && AppsUtil.hasFeature('bypass_compatibility_check')) {
					workflow = DownloadWorkflow.Kaizen; // eslint-disable-line no-param-reassign
				}
				this.handleAnalyticsEvent({
					eventObj: {
						type: 'render',
						subtype: 'downloading'
					},
					updatedWorkflow: workflow
				});

				if (workflow === DownloadWorkflow.Incompatible || workflow === DownloadWorkflow.ErrorFFCDataEmpty) {
					if (clearHelpTimeouts) {
						clearHelpTimeouts();
					}
					if (source === DOWNLOAD_CONTROLLER_USER_SOURCE.APP_PICKER_DOWNLOAD_PAGE) {
						if (AppsUtil.hasFeature('temp_cch_10855')) {
							window._uxa = window._uxa || [];
							window._uxa.push(['trackPageview', this.appPickerUrl]);
						}
						history.replace(this.appPickerUrl);
						showIncompatibilityDialog && showIncompatibilityDialog(sapCode);
					}
					else {
						if (workflow === DownloadWorkflow.ErrorFFCDataEmpty) {
							// Handle the case of 64-bit only app on 32-bit machine.
							this.handleAnalyticsEvent({
								eventObj: {
									type: 'render',
									subtype: 'downloading'
								},
								updatedWorkflow: DownloadWorkflow.Incompatible
							});
						}
						this.setState({workflow});
					}
				}
				else if (workflow !== DownloadWorkflow.ErrorFFCFetchFail) {
					const id = productId;
					const product = {
						id,
						sapCode,
						name : AppsUtil.getFFCProductConfig(id).displayName
					};
					this.setState({workflow, product});
					this.handleDownloadEvent();
				}

				/* Handle Get Help widget related logic */

				// Set page type
				let pageType;
				switch (source) {
					case DOWNLOAD_CONTROLLER_USER_SOURCE.SINGLE_APP_POST_PURCHASE_PAGE:
						pageType = TAGS.PAGE.SINGLE_APP_PLAN;
						break;
					case DOWNLOAD_CONTROLLER_USER_SOURCE.LOGGED_OUT_CAMPAIGN_PAGE:
						pageType = TAGS.PAGE.CAMPAIGN_PAGE;
						break;
					default:
						pageType = TAGS.PAGE.DOWNLOAD;
				}

				// Update Get Help config
				const updatingList = {
					listId: LIST_IDS.FAQ_AND_HELP,
					pageType: [pageType],
					installWorkflowTags: HelpPopoverUtil.getInstallWorkflowTag(hasThor),
					compatibilityTags: HelpPopoverUtil.getCompatibilityTags([AppsUtil.getSapCodeFromProductId(productId)])
				};
				if (HelpPopoverUtil.updateHelpPopoverConfig) {
					HelpPopoverUtil.updateHelpPopoverConfig(undefined, [updatingList]);
				}
			});
		}
		/**
		 * Add app picker related classes, if not already added.
		 * This will be used when the Download Controller is used directly without the AppPickerController
		 * For eg: in Single app starter url case
		 * */
		if (source === DOWNLOAD_CONTROLLER_USER_SOURCE.SINGLE_APP_POST_PURCHASE_PAGE) {
			AppPickerUtil.addAppPickerClassesAndEvents(this.ccIconEvent, this.chatEvent, this.handleCCEnter);
		}
	}

	componentWillUnmount() {
		this._isMounted = false;
		AppPickerUtil.removeAppPickerClassesAndEvents(this.ccIconEvent, this.chatEvent, this.handleCCEnter);
		CSSUtil.clearCoachMarkDisplayTimeout();
		CSSUtil.hideInstallerPopUp(0);
	}

	handleAnalyticsEvent = ({eventObj, updatedWorkflow, contentObj}: AnalyticObjectInterface) => {
		const {analyticsData = {}} = this.props;
		const workflow = updatedWorkflow || this.state.workflow;
		if (workflow === DownloadWorkflow.ErrorFFCFetchFail || workflow === DownloadWorkflow.ErrorFFCDataEmpty) {
			return;
		}
		const analytics = new AppPickerAnalytics(this.props.environment);
		analytics.initAnalytics();
		const sapCode = AppsUtil.getSapCodeFromProductId(this.props.productId);
		let event = {
			subcategory: CCDUtil.getCampaignWorkflowSubcategory(),
			value: this.getEventValue(workflow, sapCode),
			...eventObj,
		};

		let ui = {
			view_type : analytics.getPageTypeString() // eslint-disable-line camelcase
		};
		if (analyticsData) {
			event = {
				...analyticsData.event,
				...event,
			};
			ui = {
				...ui,
				...analyticsData.ui
			};
		}
		const content = {
			id: sapCode,
			name: AppsUtil.getProductDisplayName(this.props.productId) || AppPickerUtil.getDisplayName(sapCode),
			type: this.getCompatibilityString(workflow, analytics),
			version: CompatibilityUtil.getSupportedAppVersion(sapCode),
			...contentObj
		};
		analytics.genericAnalyticEvent({event, content, exp: analyticsData.exp, env: analyticsData.env, ui});
	}

	getEventValue = (workflow: string) => {
		let eventValue = '';
		if (workflow === DownloadWorkflow.KaizenOldVersion || workflow === DownloadWorkflow.ThorOldVersion) {
			eventValue = AppsUtil.stringConcatWithPipe(eventValue, 'Older App version downloaded');
		}
		if (!CompatibilityUtil.isCCDSupported() && (workflow === DownloadWorkflow.Kaizen || workflow === DownloadWorkflow.KaizenOldVersion)) {
			eventValue = AppsUtil.stringConcatWithPipe(eventValue, 'Older Thor/CCD version downloaded');
		}
		return eventValue === '' ? null : eventValue;
	};

	getCompatibilityString = (workflow: string, analytics: Object) => {
		let contentType = analytics.getCompatibilityString(true, true);
		if (workflow === DownloadWorkflow.KaizenOldVersion || workflow === DownloadWorkflow.ThorOldVersion) {
			contentType = analytics.getCompatibilityString(false, true);
		}
		if (workflow === DownloadWorkflow.Incompatible) {
			contentType = analytics.getCompatibilityString(false, false);
		}
		return contentType;
	};

	handleDownloadEvent = (isRetry: ?boolean, kaizenDnld: ?boolean) => {
		const {analyticsData = {}, environment, productId} = this.props;
		const {event = {}, env = {}, ui = {}} = analyticsData;
		const appsAnalytics = new AppsAnalytics(environment);
		appsAnalytics.initAnalytics();
		const appConfig = AppsUtil.getFFCProductConfig(productId);
		appConfig.entitled = AppsUtil.isAppEntitledNew(appConfig, appConfig.id, appConfig.type);
		appConfig.sapCode = appConfig.id;
		let subType;
		let eventValue;
		if (isRetry) {
			subType = 'retry';
		}
		else if (kaizenDnld) {
			subType = 'kaizen-download';
		}
		if (URL.getParam(AUTO_DOWNLOAD_QUERY_PARAM.QUERY_PARAM) === AUTO_DOWNLOAD_QUERY_PARAM.QUERY_VALUE_AUTO_WITH_ACTION) {
			eventValue = AUTO_DOWNLOAD_QUERY_PARAM.QUERY_VALUE_AUTO_WITH_ACTION;
		}
		if (URL.getParam(AUTO_DOWNLOAD_QUERY_PARAM.QUERY_PARAM) === AUTO_DOWNLOAD_QUERY_PARAM.QUERY_VALUE_AUTO) {
			eventValue = AUTO_DOWNLOAD_QUERY_PARAM.QUERY_VALUE_AUTO;
		}
		const analytics = new AppPickerAnalytics(this.props.environment);
		appsAnalytics.onAppDownloadEvent(appConfig, ui.view_type || analytics.getPageTypeString(), subType, event.workflow, env, eventValue);
	};

	renderLoader = () => (
		<div className={DownloadControllerScss.loader}>
			<Wait size='L' />
		</div>
	);

	onCCDNotSupported = () => {
		const guid = uuid.v4();
		const url = AppsUtil.openExternalLink(getLocalizedURL(getConfigApps('EXTERNAL_URLS').CCD_LEARN_MORE_LINK, this.props.locale), this.state.product.sapCode, guid);
		this.handleAnalyticsEvent({
			eventObj: {
				subcategory: `${CCDUtil.getCampaignWorkflowSubcategory()}:Toast`,
				type:'click',
				subtype: 'learn-more',
				value : 'Older Thor/CCD version downloaded',
				guid
			},
			contentObj: {
				action: url
			}
		});
	}

	onMuseLearnMore = () => {
		const guid = uuid.v4();
		AppsUtil.openExternalLink(AppsUtil.getCCDGoURL(this.props.locale, getConfigApps('EXTERNAL_URLS').MUSE_EOL_LINK), this.state.product.sapCode, guid);
	}

	onLearnMoreSysReqtClick = (is32BitToast: ?boolean) => {
		const guid = uuid.v4();
		const {locale} = this.props;
		const {
			product: {id, sapCode}
		} = this.state;
		const url = AppsUtil.openExternalLink(
			AppPickerUtil.getSystemRequirementLink(sapCode) || AppsUtil.getCCDGoURL(locale, AppsUtil.getSystemReqURL(id)),
			sapCode,
			guid
		);
		this.handleAnalyticsEvent({
			eventObj: {
				subcategory: `${CCDUtil.getCampaignWorkflowSubcategory()}:Toast`,
				type:'click',
				subtype: 'learn-more',
				value: is32BitToast ? '32-bit Older App version downloaded' : 'Older App version downloaded',
				guid
			},
			contentObj: {
				action: url
			}
		});
	}

	onGetHelp = () => {
		const {source} = this.props;
		if (this.props.openGetHelpWidget && HelpPopoverUtil.openGetHelpPopover) {
			const sourceParam = (source === DOWNLOAD_CONTROLLER_USER_SOURCE.LOGGED_OUT_CAMPAIGN_PAGE)
				? GET_HELP_OPEN_SOURCES.LOGGED_OUT_CAMPAIGN_GET_HELP_CLICK
				: GET_HELP_OPEN_SOURCES.GET_HELP_DOWNLOAD_PAGE;
			HelpPopoverUtil.openGetHelpPopover(sourceParam);
			this.handleAnalyticsEvent({
				eventObj: {
					type: 'click',
					subtype: AppsUtil.isLoggedOutCampaignExp
						? 'get-help'
						: 'help'
				}
			});
		}
		else {
			const guid = uuid.v4();
			const url = AppsUtil.openExternalLink(getLocalizedURL('https://helpx.adobe.com/download-install/using/download-creative-cloud-apps.html', this.props.locale), this.state.product.sapCode, guid);
			this.handleAnalyticsEvent({
				eventObj: {
					type: 'click',
					subtype: AppsUtil.isLoggedOutCampaignExp
						? 'get-help'
						: 'help',
					guid
				},
				contentObj: {
					action: url
				}
			});
		}
	}

	displayCoachMarkWithAnimation = (isRestartAnimation: boolean, onHideCallback: Function, isNonInstallerFileDownload?: ?boolean) => {
		CSSUtil.addAnimatedInstallerPopup(<DownloadCoachMark isAllAppsPicker={true} isNonInstallerFileDownload={!!isNonInstallerFileDownload} intl={this.props.intl}/>, 1500, isRestartAnimation, onHideCallback, true);
	}

	getMessagesList = () => {
		const messages = [];
		if (this.state.workflow === DownloadWorkflow.KaizenOldVersion || this.state.workflow === DownloadWorkflow.ThorOldVersion) {
			messages.push({variant: MESSAGE_BOX_VARIANT.APP_OLD_VERSION, callback: () => {this.onLearnMoreSysReqtClick();}});
		}
		if (this.state.product.sapCode === 'KCCC' && !CompatibilityUtil.isCCDSupported() && (this.state.workflow === DownloadWorkflow.Kaizen || this.state.workflow === DownloadWorkflow.KaizenOldVersion)) {
			messages.push({variant: MESSAGE_BOX_VARIANT.CCD_NOT_SUPPORTED, callback: this.onCCDNotSupported});
		}
		if (this.state.product.sapCode === 'MUSE') {
			messages.push({variant: MESSAGE_BOX_VARIANT.MUSE_EOL, callback: this.onMuseLearnMore});
		}
		return messages;
	}

	isPageFooterVisible() {
		if ([DOWNLOAD_CONTROLLER_USER_SOURCE.LOGGED_OUT_KCCC_PAGE, DOWNLOAD_CONTROLLER_USER_SOURCE.LOGGED_OUT_CAMPAIGN_PAGE].includes(this.props.source)) {
			return false;
		}
		return true;
	}

	showDownloadPageFooter() {
		const {source, productId} = this.props;
		const sapCode = AppsUtil.getSapCodeFromProductId(productId);
		if (source === DOWNLOAD_CONTROLLER_USER_SOURCE.SINGLE_APP_POST_PURCHASE_PAGE && sapCode !== 'KCCC') {
			return true;
		}
		return false;
	}

	renderFooter() {
		const {hasThor, environment, intl, source, productId} = this.props;
		const sapCode = AppsUtil.getSapCodeFromProductId(productId);
		if (this.showDownloadPageFooter()) {
			return (
				<div className={`${DownloadControllerScss.footer} ${this.isPageFooterVisible() ? DownloadControllerScss.pageFooterVisible : DownloadControllerScss.pageFooterInVisible}`}>
					<DownloadPageFooter
						sapCode={sapCode}
						hasThor={hasThor}
						environment={environment}
						source={source}
						formatMessage={intl.formatMessage}
					/>
				</div>
			);
		}
	}

	getClassName() {
		switch (this.props.source) {
			case DOWNLOAD_CONTROLLER_USER_SOURCE.LOGGED_OUT_CAMPAIGN_PAGE:
				return DownloadControllerScss.loggedOutCampaign;
			case DOWNLOAD_CONTROLLER_USER_SOURCE.LOGGED_OUT_KCCC_PAGE:
				return DownloadControllerScss.loggedOutTrailhead;
			case DOWNLOAD_CONTROLLER_USER_SOURCE.SINGLE_APP_POST_PURCHASE_PAGE:
				return DownloadControllerScss.singleAppPostPurchase;
			case DOWNLOAD_CONTROLLER_USER_SOURCE.APP_PICKER_DOWNLOAD_PAGE:
				return DownloadControllerScss.appPickerPostPurchase;
		}
		return '';
	}

	render() {
		const {workflow, product} = this.state;
		const {className, source, intl, productId, environment, locale, hasThor, history} = this.props;
		let bodyElem;
		if (workflow !== LOADING_WORKFLOW) {
			/*
				cch-10509
				Removing loader here since loader is not being
				removed in Main.js for campaign pages
			*/
			CSSUtil.hideTransitionLoader();
			if (workflow === DownloadWorkflow.Kaizen || workflow === DownloadWorkflow.KaizenOldVersion) {
				bodyElem = <KaizenView
					className={className}
					source={source}
					product={product}
					intl={intl}
					messagesList = {this.getMessagesList()}
					displayCoachMark = {this.displayCoachMarkWithAnimation}
					handleAnalyticsEvent = {this.handleAnalyticsEvent}
					onGetHelp={this.onGetHelp}
					downloadViaCCD={this.downloadViaCCD}
					handleDownloadEvent = {this.handleDownloadEvent}
				/>;
			}
			if (workflow === DownloadWorkflow.Thor || workflow === DownloadWorkflow.ThorOldVersion) {
				bodyElem = <ThorView
					className={className}
					source={source}
					product={product}
					intl={intl}
					messagesList = {this.getMessagesList()}
					displayCoachMark = {this.displayCoachMarkWithAnimation}
					handleAnalyticsEvent = {this.handleAnalyticsEvent}
					onGetHelp={this.onGetHelp}
					handleDownloadEvent = {this.handleDownloadEvent}
				/>;
			}
			if (workflow === DownloadWorkflow.Incompatible || workflow === DownloadWorkflow.ErrorFFCDataEmpty) {
				bodyElem = <IncompatibleView
					className={className}
					productId={productId}
					sapCode={AppsUtil.getSapCodeFromProductId(productId)}
					source={source}
					onGetHelp={this.onGetHelp}
					environment={environment}
					formatMessage={intl.formatMessage}
					locale={locale}
					hasThor={hasThor}
					history={history}
				/>;
			}
			if (workflow === DownloadWorkflow.FileOpen) {
				bodyElem = <OpenFileView
					className={className}
					source={source}
					product={product}
					intl={intl}
					isFileDownloadSuccess={this.isFileDownloadSuccess}
					displayCoachMark = {this.displayCoachMarkWithAnimation}
					handleAnalyticsEvent = {this.handleAnalyticsEvent}
					onGetHelp={this.onGetHelp}
				/>;
			}
			return (
				<div className={`${DownloadControllerScss.downloadController} ${this.getClassName()}`}>
					{bodyElem}
					{this.renderFooter()}
				</div>
			);
		}
		return this.renderLoader();
	}
}
